import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trophy, Crown, Medal, TrendingUp, Percent, Coins, Hash, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PERIODS = [{ id: "all", label: "All Time" }, { id: "30d", label: "30 Days" }, { id: "7d", label: "7 Days" }];
const METRICS = [
  { id: "win_rate", label: "Win Rate", icon: Percent, fmt: (r) => `${r.win_rate}%` },
  { id: "return_pct", label: "Return %", icon: TrendingUp, fmt: (r) => (r.return_pct == null ? "—" : `${r.return_pct > 0 ? "+" : ""}${r.return_pct}%`) },
  { id: "net", label: "Net P&L", icon: Coins, fmt: (r) => money(r.net) },
  { id: "trades", label: "Trades", icon: Hash, fmt: (r) => r.trades },
];

const WSLeaderboard = () => {
  const [period, setPeriod] = useState("all");
  const [metric, setMetric] = useState("win_rate");
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const r = await fetch(`${API}/leaderboard/traders?period=${period}`, { credentials: "include" });
      setRows(r.ok ? (await r.json()).traders || [] : []);
    } catch { setRows([]); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    if (!rows) return [];
    const val = (r) => (metric === "return_pct" ? (r.return_pct ?? -Infinity) : r[metric]);
    return [...rows].sort((a, b) => (val(b) - val(a)) || (b.net - a.net));
  }, [rows, metric]);

  const activeMetric = METRICS.find((m) => m.id === metric);
  const podium = sorted.slice(0, 3);
  const podiumOrder = [podium[1], podium[0], podium[2]].filter(Boolean); // 2nd, 1st, 3rd

  const Avatar = ({ u, size = "w-11 h-11", text = "text-[15px]" }) =>
    u.picture
      ? <img src={u.picture} alt={u.name} className={`${size} rounded-full object-cover border border-[color:var(--ws-border-strong)]`} />
      : <div className={`${size} ${text} rounded-full bg-[rgba(59,130,246,0.2)] flex items-center justify-center font-bold text-[color:var(--ws-blue-bright)]`}>{u.name?.[0]?.toUpperCase() || "T"}</div>;

  return (
    <div className="max-w-[1080px] mx-auto px-8 py-8" data-testid="ws-leaderboard">
      <div className="flex items-center gap-3 mb-2 ws-fade-up">
        <div className="ws-icon-chip w-9 h-9"><Trophy className="w-5 h-5" /></div>
        <span className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Real Performance · Live Rankings</span>
      </div>
      <h1 className="ws-heading text-[40px] font-bold mb-1 ws-title-gradient ws-fade-up" style={{ animationDelay: "40ms" }}>WS Leaderboard</h1>
      <p className="text-[13px] text-[color:var(--ws-muted)] mb-6 ws-fade-up" style={{ animationDelay: "60ms" }}>The sharpest traders on Wealth Secret — ranked by verified MT5 results.</p>

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 ws-fade-up" style={{ animationDelay: "80ms" }}>
        <div className="flex gap-1.5" data-testid="lb-metrics">
          {METRICS.map((m) => (
            <button key={m.id} onClick={() => setMetric(m.id)} data-testid={`lb-metric-${m.id}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors ${metric === m.id ? "bg-[color:var(--ws-blue)] text-white" : "ws-btn-ghost text-[color:var(--ws-muted)]"}`}>
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5" data-testid="lb-periods">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`lb-period-${p.id}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${period === p.id ? "bg-[rgba(230,194,90,0.18)] text-[#e6c25a] border border-[rgba(230,194,90,0.4)]" : "ws-btn-ghost text-[color:var(--ws-muted)]"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {rows === null && <div className="ws-panel p-10 text-center text-[color:var(--ws-muted)]" data-testid="lb-loading">Loading rankings…</div>}
      {rows && rows.length === 0 && <div className="ws-panel p-10 text-center text-[color:var(--ws-muted)]" data-testid="lb-empty">No ranked traders yet for this period. Connect an MT5 account and log at least 3 trades to appear.</div>}

      {rows && rows.length > 0 && (
        <>
          {/* Podium */}
          <div className="ws-lbp-wrap ws-fade-up" style={{ animationDelay: "100ms" }} data-testid="lb-podium">
            {podiumOrder.map((u) => {
              const place = u.rank;
              return (
                <div key={u.user_id} className={`ws-lbp-card place-${place} ${u.you ? "ws-lbp-you" : ""}`}>
                  <div className={`ws-lbp-medal m${place}`}>
                    {place === 1 ? <Crown className="w-4 h-4" /> : <Medal className="w-4 h-4" />}
                    <span>{place}</span>
                  </div>
                  <Avatar u={u} size="w-16 h-16" text="text-[22px]" />
                  <div className="ws-lbp-name">{u.name}{u.you && <span className="ws-lbp-youtag">YOU</span>}</div>
                  <div className="ws-lbp-metric">{activeMetric.fmt(u)}</div>
                  <div className="ws-lbp-sub">{activeMetric.label}</div>
                  <div className="ws-lbp-chips">
                    <span><Percent className="w-3 h-3" />{u.win_rate}%</span>
                    <span className={u.net >= 0 ? "ws-up" : "ws-down"}>{money(u.net)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Full table */}
          <div className="ws-panel p-2 ws-fade-up" style={{ animationDelay: "140ms" }}>
            <div className="ws-lb-head">
              <span className="w-10 text-center">#</span>
              <span className="flex-1">Trader</span>
              <span className="ws-lb-col">Win Rate</span>
              <span className="ws-lb-col">Return</span>
              <span className="ws-lb-col">Net P&L</span>
              <span className="ws-lb-col hidden sm:block">Trades</span>
            </div>
            <div data-testid="lb-rows">
              {sorted.map((u, i) => (
                <div key={u.user_id} className={`ws-lb-row ${u.you ? "ws-lb-you" : ""}`} data-testid={`lb-row-${i + 1}`}>
                  <span className={`ws-lb-pos ${i < 3 ? `top${i + 1}` : ""}`}>{i + 1}</span>
                  <span className="flex-1 flex items-center gap-3 min-w-0">
                    <Avatar u={u} size="w-9 h-9" text="text-[13px]" />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-white truncate">{u.name}{u.you && <span className="text-[10px] text-[color:var(--ws-blue-bright)] ml-1.5">YOU</span>}</span>
                      <span className="block text-[11px] text-[color:var(--ws-muted)]">{u.wins}W · {u.trades - u.wins}L</span>
                    </span>
                  </span>
                  <span className={`ws-lb-col font-semibold ${u.win_rate >= 50 ? "ws-up" : "text-white"}`}>{u.win_rate}%</span>
                  <span className={`ws-lb-col font-semibold ${u.return_pct == null ? "text-[color:var(--ws-muted)]" : u.return_pct >= 0 ? "ws-up" : "ws-down"}`}>{u.return_pct == null ? "—" : `${u.return_pct > 0 ? "+" : ""}${u.return_pct}%`}</span>
                  <span className={`ws-lb-col font-bold ${u.net >= 0 ? "ws-up" : "ws-down"}`}>{money(u.net)}</span>
                  <span className="ws-lb-col text-white hidden sm:block">{u.trades}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WSLeaderboard;
